import { useCallback, useState } from 'react';

/**
 * Ordenação das tabelas inline do card de anúncio (campanhas e concorrentes).
 *
 * Mora aqui, e não dentro de cada tabela, porque as duas precisam exatamente do
 * mesmo comportamento — ciclo de três estados, ausente no fim, comparação sobre
 * o dado e não sobre o texto (CLAUDE.md §4).
 */

/** Clique no cabeçalho: nenhum → crescente → decrescente → nenhum. */
const NEXT_DIRECTION = { none: 'asc', asc: 'desc', desc: 'none' };
const NEXT_LABEL = { asc: 'ordenar crescente', desc: 'ordenar decrescente', none: 'remover ordenação' };

/**
 * @param {Array<{key,label,align,title,value}>} columns — `value(row)` devolve o
 *        DADO de comparação (número ou string), nunca o texto já formatado:
 *        "R$ 1.234,00" ordenado como string coloca R$ 9,00 depois de R$ 1.234,00.
 */
export function useTableSort(columns) {
    const [sort, setSort] = useState({ key: null, direction: 'none' });

    const toggleSort = useCallback((key) => {
        setSort((prev) => {
            const direction = prev.key === key ? NEXT_DIRECTION[prev.direction] : 'asc';
            return direction === 'none' ? { key: null, direction: 'none' } : { key, direction };
        });
    }, []);

    /**
     * @param rows        lista original (não é mutada — a cópia é feita aqui)
     * @param defaultSort ordem quando nenhuma coluna está ativa; sem ela, mantém
     *                    a ordem em que o backend mandou
     */
    const sortRows = useCallback(
        (rows, defaultSort) => {
            const list = Array.isArray(rows) ? [...rows] : [];

            if (sort.direction === 'none' || !sort.key) {
                return typeof defaultSort === 'function' ? defaultSort(list) : list;
            }

            const column = columns.find((entry) => entry.key === sort.key);
            if (!column) {
                return list;
            }

            const factor = sort.direction === 'asc' ? 1 : -1;
            list.sort((a, b) => {
                const left = column.value(a);
                const right = column.value(b);

                // Ausente vai SEMPRE para o fim, nas duas direções: um '—' no
                // topo da lista ordenada por preço não informa nada e empurra o
                // dado real para fora da vista.
                const leftMissing = left === null || left === undefined || left === '';
                const rightMissing = right === null || right === undefined || right === '';
                if (leftMissing && rightMissing) {
                    return 0;
                }
                if (leftMissing) {
                    return 1;
                }
                if (rightMissing) {
                    return -1;
                }

                if (typeof left === 'string' || typeof right === 'string') {
                    return String(left).localeCompare(String(right), 'pt-BR') * factor;
                }
                return (Number(left) - Number(right)) * factor;
            });
            return list;
        },
        [columns, sort],
    );

    return { sort, toggleSort, sortRows };
}

const DIRECTION_SPOKEN = { asc: 'ordenado crescente', desc: 'ordenado decrescente', none: 'sem ordenação' };

/**
 * Cabeçalho clicável no mesmo grid das linhas.
 *
 * O caret aparece só na coluna ativa: seta em todas, numa linha de 9px, vira
 * ruído. Para quem usa leitor de tela, o estado vai no `aria-label` — e não em
 * `aria-sort`, que só vale em `columnheader` de tabela; aqui as linhas são um
 * grid CSS, não um `<table>`.
 */
export const SortableHeader = ({ columns, sort, onToggle, gridClass, className = '' }) => (
    <div
        className={`${gridClass} border-b border-mono-200 px-1 py-1 text-[9px] font-semibold uppercase tracking-wide text-mono-400 dark:border-mono-800 ${className}`}
    >
        {columns.map((column) => {
            const active = sort.key === column.key && sort.direction !== 'none';
            const direction = active ? sort.direction : 'none';
            return (
                <button
                    key={column.key}
                    type="button"
                    onClick={() => onToggle(column.key)}
                    title={`${column.title || column.label} — clique para ${NEXT_LABEL[NEXT_DIRECTION[direction]]}`}
                    aria-label={`${column.label}, ${DIRECTION_SPOKEN[direction]}. Ativar para ${NEXT_LABEL[NEXT_DIRECTION[direction]]}.`}
                    className={`flex min-w-0 items-center gap-0.5 uppercase tracking-wide transition-colors hover:text-black dark:hover:text-white ${
                        column.align === 'right' ? 'justify-end' : 'justify-start'
                    } ${active ? 'text-black dark:text-white' : ''}`}
                >
                    <span className="truncate">{column.label}</span>
                    {active ? (
                        <span aria-hidden="true" className="shrink-0">
                            {direction === 'asc' ? '▲' : '▼'}
                        </span>
                    ) : null}
                </button>
            );
        })}
    </div>
);
